import React, { useEffect, useMemo, useState } from 'react';
import { Link as Anchor } from 'react-router-dom';
import './NetSalesOverview.css';
import baseURL from '../../url';
import { getProductMoneda } from '../../moneda';

const ORDER_STATUS_LABELS = {
    Pending: 'Pendiente',
    Pendiente: 'Pendiente',
    Paid: 'Pagado',
    Pagado: 'Pagado',
    Shipped: 'Enviado',
    Enviado: 'Enviado',
    Received: 'Recibido',
    Recibido: 'Recibido',
    Delivered: 'Entregado',
    Entregado: 'Entregado',
    Rejected: 'Cancelado',
    Rechazado: 'Cancelado',
    Cancelled: 'Cancelado',
    Canceled: 'Cancelado',
    Cancelado: 'Cancelado',
};

const MONTH_LABELS = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
];

const SHORT_MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const SHORT_WEEK_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const CHART_WIDTH = 820;
const CHART_HEIGHT = 160;
const CHART_PADDING_X = 18;
const CHART_PADDING_Y = 28;

function getOrderStatusLabel(status) {
    return ORDER_STATUS_LABELS[status] || status || 'Pendiente';
}

function parseDateInput(dateValue) {
    if (!dateValue) {
        return null;
    }

    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date) {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
}

function endOfDay(date) {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next;
}

function startOfWeek(date) {
    const next = startOfDay(date);
    const day = next.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    next.setDate(next.getDate() + diff);
    return next;
}

function endOfWeek(date) {
    const next = startOfWeek(date);
    next.setDate(next.getDate() + 6);
    return endOfDay(next);
}

function startOfMonth(year, month) {
    return new Date(year, month, 1, 0, 0, 0, 0);
}

function endOfMonth(year, month) {
    return new Date(year, month + 1, 0, 23, 59, 59, 999);
}

function startOfYear(year) {
    return new Date(year, 0, 1, 0, 0, 0, 0);
}

function endOfYear(year) {
    return new Date(year, 11, 31, 23, 59, 59, 999);
}

function formatMoney(value) {
    const amount = Number(value || 0);
    const absoluteAmount = Math.abs(amount)
        .toFixed(0)
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `${amount < 0 ? '-' : ''}${getProductMoneda()} ${absoluteAmount}`;
}

function formatCompactMoney(value) {
    const amount = Number(value || 0);
    const absoluteAmount = Math.abs(amount);
    const prefix = amount < 0 ? '-' : '';

    if (absoluteAmount >= 1000000) {
        return `${prefix}${getProductMoneda()}${(absoluteAmount / 1000000).toFixed(absoluteAmount % 1000000 === 0 ? 0 : 1)}M`;
    }

    if (absoluteAmount >= 1000) {
        return `${prefix}${getProductMoneda()}${(absoluteAmount / 1000).toFixed(absoluteAmount % 1000 === 0 ? 0 : 1)}k`;
    }

    return `${prefix}${getProductMoneda()}${absoluteAmount.toFixed(0)}`;
}

function formatRangeDate(date) {
    return date.toLocaleDateString('es-CL', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

function formatShortRangeDate(date) {
    return date.toLocaleDateString('es-CL', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function formatLocalDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function isRecognizedWhatsappSale(order) {
    return Number(order?.salesRecognized) === 1;
}

function isCancelledWhatsappSale(order) {
    return getOrderStatusLabel(order?.estado) === 'Cancelado' && Number(order?.salesRecognized) === 1;
}

function getRecognizedDate(order) {
    return parseDateInput(order?.salesRecognizedAt || order?.createdAt);
}

function getReversedDate(order) {
    return parseDateInput(order?.salesReversedAt || order?.salesRecognizedAt || order?.createdAt);
}

function buildLedgerEvents(orders) {
    const events = [];

    orders.forEach((order) => {
        const total = Number(order?.total || 0);
        if (!Number.isFinite(total) || total === 0) {
            return;
        }

        if (isRecognizedWhatsappSale(order)) {
            const recognizedDate = getRecognizedDate(order);
            if (recognizedDate) {
                events.push({
                    type: 'recognized',
                    amount: total,
                    date: recognizedDate,
                    order,
                });
            }
        }

        if (isCancelledWhatsappSale(order)) {
            const reversedDate = getReversedDate(order);
            if (reversedDate) {
                events.push({
                    type: 'reversed',
                    amount: -total,
                    date: reversedDate,
                    order,
                });
            }
        }
    });

    return events.sort((left, right) => left.date.getTime() - right.date.getTime());
}

function sumEventsInRange(events, start, end) {
    return events.reduce((total, event) => {
        const time = event.date.getTime();
        if (time >= start.getTime() && time <= end.getTime()) {
            return total + event.amount;
        }
        return total;
    }, 0);
}

function buildGeometry(series) {
    const plotWidth = CHART_WIDTH - (CHART_PADDING_X * 2);
    const plotHeight = CHART_HEIGHT - (CHART_PADDING_Y * 2);
    const values = series.map((item) => Number(item.value || 0));
    const minValue = Math.min(...values, 0);
    const maxValue = Math.max(...values, 0);

    if (maxValue === 0 && minValue === 0) {
        const stepX = series.length > 1 ? plotWidth / (series.length - 1) : 0;
        const points = series.map((item, index) => ({
            ...item,
            x: CHART_PADDING_X + (stepX * index),
            y: CHART_PADDING_Y,
        }));
        const pointsText = points.map((point) => `${point.x},${point.y}`).join(' ');
        return {
            points,
            pointsText,
            areaPath: `M ${CHART_PADDING_X} ${CHART_PADDING_Y} L ${pointsText} L ${CHART_WIDTH - CHART_PADDING_X} ${CHART_PADDING_Y} Z`,
            zeroLineY: CHART_PADDING_Y,
            tone: 'neutral',
        };
    }

    const range = maxValue - minValue || 1;
    const stepX = series.length > 1 ? plotWidth / (series.length - 1) : 0;
    const mapY = (value) => CHART_PADDING_Y + (((maxValue - value) / range) * plotHeight);
    const zeroLineY = mapY(0);

    const points = series.map((item, index) => ({
        ...item,
        x: CHART_PADDING_X + (stepX * index),
        y: mapY(item.value),
    }));

    const pointsText = points.map((point) => `${point.x},${point.y}`).join(' ');
    const areaPath = [
        `M ${points[0].x} ${zeroLineY}`,
        ...points.map((point) => `L ${point.x} ${point.y}`),
        `L ${points[points.length - 1].x} ${zeroLineY}`,
        'Z',
    ].join(' ');

    const totalValue = values.reduce((sum, value) => sum + value, 0);
    const tone = totalValue > 0 ? 'positive' : totalValue < 0 ? 'negative' : 'neutral';

    return {
        points,
        pointsText,
        areaPath,
        zeroLineY,
        tone,
    };
}

function getPointToneClass(value) {
    if (value > 0) {
        return 'is-positive';
    }
    if (value < 0) {
        return 'is-negative';
    }
    return 'is-neutral';
}

function buildWeekSeries(events, weekStart) {
    const start = startOfWeek(weekStart);
    const end = endOfWeek(start);
    const buckets = [];

    for (let index = 0; index < 7; index += 1) {
        const bucketDate = new Date(start);
        bucketDate.setDate(start.getDate() + index);
        buckets.push({
            key: formatLocalDateKey(startOfDay(bucketDate)),
            label: SHORT_WEEK_LABELS[index],
            value: 0,
            title: formatRangeDate(bucketDate),
        });
    }

    const map = new Map(buckets.map((bucket) => [bucket.key, bucket]));
    events.forEach((event) => {
        if (event.date >= start && event.date <= end) {
            const key = formatLocalDateKey(startOfDay(event.date));
            const bucket = map.get(key);
            if (bucket) {
                bucket.value += event.amount;
            }
        }
    });

    return {
        start,
        end,
        series: buckets,
    };
}

function getIsoDateKey(date) {
    return formatLocalDateKey(startOfDay(date));
}

function buildWeekOptionsForYear(year) {
    const options = [];
    const cursor = startOfWeek(new Date(year, 0, 1));
    const yearEnd = endOfYear(year);

    while (cursor <= yearEnd) {
        const start = new Date(cursor);
        const end = endOfWeek(start);

        options.push({
            value: getIsoDateKey(start),
            start,
            label: `${formatShortRangeDate(start)} - ${formatShortRangeDate(end)}`,
        });

        cursor.setDate(cursor.getDate() + 7);
    }

    return options.sort((left, right) => right.start.getTime() - left.start.getTime());
}

function buildMonthSeries(events, year, month) {
    const start = startOfMonth(year, month);
    const end = endOfMonth(year, month);
    const buckets = [];
    const cursor = new Date(start);
    let weekNumber = 1;

    while (cursor <= end) {
        const bucketStart = new Date(cursor);
        const bucketEnd = endOfDay(new Date(Math.min(
            end.getTime(),
            endOfWeek(bucketStart).getTime()
        )));
        buckets.push({
            key: getIsoDateKey(bucketStart),
            label: `Sem ${weekNumber}`,
            value: 0,
            title: `${formatShortRangeDate(bucketStart)} - ${formatShortRangeDate(bucketEnd)}`,
            start: bucketStart,
            end: bucketEnd,
        });

        cursor.setTime(bucketEnd.getTime());
        cursor.setDate(cursor.getDate() + 1);
        cursor.setHours(0, 0, 0, 0);
        weekNumber += 1;
    }

    events.forEach((event) => {
        if (event.date >= start && event.date <= end) {
            const bucket = buckets.find((item) => event.date >= item.start && event.date <= item.end);
            if (bucket) {
                bucket.value += event.amount;
            }
        }
    });

    return {
        start,
        end,
        series: buckets,
    };
}

function buildYearSeries(events, year) {
    const start = startOfYear(year);
    const end = endOfYear(year);
    const buckets = SHORT_MONTH_LABELS.map((label, monthIndex) => ({
        key: `${year}-${monthIndex}`,
        label,
        value: 0,
        title: `${MONTH_LABELS[monthIndex]} ${year}`,
    }));

    events.forEach((event) => {
        if (event.date >= start && event.date <= end) {
            buckets[event.date.getMonth()].value += event.amount;
        }
    });

    return {
        start,
        end,
        series: buckets,
    };
}

export default function NetSalesOverview() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const today = useMemo(() => new Date(), []);
    const [viewMode, setViewMode] = useState('month');
    const [selectedYear, setSelectedYear] = useState(today.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
    const [selectedWeekStart, setSelectedWeekStart] = useState(getIsoDateKey(startOfWeek(today)));

    useEffect(() => {
        const loadOrders = async () => {
            try {
                const response = await fetch(`${baseURL}/pedidoGet.php`, {
                    method: 'GET',
                });
                const data = await response.json();
                setOrders(Array.isArray(data.pedidos) ? data.pedidos : []);
            } catch (error) {
                console.error('Error al cargar ventas por WhatsApp:', error);
                setOrders([]);
            } finally {
                setLoading(false);
            }
        };

        loadOrders();
    }, []);

    const ledgerEvents = useMemo(() => buildLedgerEvents(orders), [orders]);

    const yearOptions = useMemo(() => {
        const years = new Set([today.getFullYear()]);
        ledgerEvents.forEach((event) => years.add(event.date.getFullYear()));
        return Array.from(years).sort((left, right) => right - left);
    }, [ledgerEvents, today]);

    const weekOptions = useMemo(() => buildWeekOptionsForYear(selectedYear), [selectedYear]);

    useEffect(() => {
        if (viewMode !== 'week' || weekOptions.length === 0) {
            return;
        }

        const currentWeekKey = getIsoDateKey(startOfWeek(today));
        const selectedExists = weekOptions.some((week) => week.value === selectedWeekStart);

        if (!selectedExists) {
            const fallbackWeek = weekOptions.find((week) => week.value === currentWeekKey) || weekOptions[0];
            setSelectedWeekStart(fallbackWeek.value);
        }
    }, [selectedWeekStart, selectedYear, today, viewMode, weekOptions]);

    const summary = useMemo(() => {
        const currentDayStart = startOfDay(today);
        const currentDayEnd = endOfDay(today);
        const currentWeekStart = startOfWeek(today);
        const currentWeekEnd = endOfWeek(today);
        const currentMonthStart = startOfMonth(today.getFullYear(), today.getMonth());
        const currentMonthEnd = endOfMonth(today.getFullYear(), today.getMonth());
        const currentYearStart = startOfYear(today.getFullYear());
        const currentYearEnd = endOfYear(today.getFullYear());

        return {
            today: sumEventsInRange(ledgerEvents, currentDayStart, currentDayEnd),
            week: sumEventsInRange(ledgerEvents, currentWeekStart, currentWeekEnd),
            month: sumEventsInRange(ledgerEvents, currentMonthStart, currentMonthEnd),
            year: sumEventsInRange(ledgerEvents, currentYearStart, currentYearEnd),
        };
    }, [ledgerEvents, today]);

    const chartData = useMemo(() => {
        let rangeData;

        if (viewMode === 'week') {
            rangeData = buildWeekSeries(ledgerEvents, new Date(`${selectedWeekStart}T00:00:00`));
        } else if (viewMode === 'year') {
            rangeData = buildYearSeries(ledgerEvents, selectedYear);
        } else {
            rangeData = buildMonthSeries(ledgerEvents, selectedYear, selectedMonth);
        }

        const total = rangeData.series.reduce((sum, item) => sum + item.value, 0);
        const geometry = buildGeometry(rangeData.series);

        return {
            ...rangeData,
            total,
            ...geometry,
        };
    }, [ledgerEvents, selectedMonth, selectedWeekStart, selectedYear, viewMode]);

    const chartRangeLabel = `${formatRangeDate(chartData.start)} - ${formatRangeDate(chartData.end)}`;
    const chartLabelMinWidth = viewMode === 'year' ? '76px' : '64px';

    return (
        <section className='salesOverviewPanel'>
            <div className='salesOverviewHeader'>
                <div>
                    <h3>Resumen neto de ventas</h3>
                    <p>Los pedidos por WhatsApp suman solo cuando confirmas el pago, y si luego cancelas uno confirmado se descuenta automáticamente.</p>
                </div>
                <div className='salesOverviewHeaderActions'>
                    <Anchor to='/dashboard/pedidos' className='salesOverviewLink'>Ver pedidos</Anchor>
                </div>
            </div>

            <div className='salesSummaryGrid'>
                <article className='salesSummaryCard'>
                    <span>Hoy</span>
                    <strong className={getPointToneClass(summary.today)}>{loading ? '...' : formatMoney(summary.today)}</strong>
                </article>
                <article className='salesSummaryCard'>
                    <span>Esta semana</span>
                    <strong className={getPointToneClass(summary.week)}>{loading ? '...' : formatMoney(summary.week)}</strong>
                </article>
                <article className='salesSummaryCard'>
                    <span>Este mes</span>
                    <strong className={getPointToneClass(summary.month)}>{loading ? '...' : formatMoney(summary.month)}</strong>
                </article>
                <article className='salesSummaryCard'>
                    <span>Este año</span>
                    <strong className={getPointToneClass(summary.year)}>{loading ? '...' : formatMoney(summary.year)}</strong>
                </article>
            </div>

            <div className='salesChartCard'>
                <div className='salesChartHeader'>
                    <div>
                        <h4>Gráfico de rendimiento</h4>
                        <p>{chartRangeLabel}</p>
                    </div>
                    <div className='salesChartControls'>
                        <div className='salesChartFilters'>
                            <button type='button' className={viewMode === 'week' ? 'active' : ''} onClick={() => setViewMode('week')}>Semana</button>
                            <button type='button' className={viewMode === 'month' ? 'active' : ''} onClick={() => setViewMode('month')}>Mes</button>
                            <button type='button' className={viewMode === 'year' ? 'active' : ''} onClick={() => setViewMode('year')}>Año</button>
                        </div>
                        <div className='salesChartSelectors'>
                            {viewMode === 'week' && (
                                <select value={selectedWeekStart} onChange={(event) => setSelectedWeekStart(event.target.value)}>
                                    {weekOptions.map((week) => (
                                        <option key={week.value} value={week.value}>{week.label}</option>
                                    ))}
                                </select>
                            )}
                            <select value={selectedYear} onChange={(event) => setSelectedYear(Number(event.target.value))}>
                                {yearOptions.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                            {viewMode === 'month' && (
                                <select value={selectedMonth} onChange={(event) => setSelectedMonth(Number(event.target.value))}>
                                    {MONTH_LABELS.map((month, index) => (
                                        <option key={month} value={index}>{month}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>
                </div>

                <div className='salesChartMeta'>
                    <strong className={chartData.tone === 'positive' ? 'is-positive' : chartData.tone === 'negative' ? 'is-negative' : 'is-neutral'}>
                        {loading ? '...' : formatMoney(chartData.total)}
                    </strong>
                    <span className='salesChartRange'>Rango consultado: {chartRangeLabel}</span>
                </div>

                <div className='salesLineChartCard'>
                    <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} className='salesLineChartSvg' role='img' aria-label='Gráfico de ventas netas'>
                        <defs>
                            <linearGradient id='salesAreaGradient' x1='0' y1='0' x2='0' y2='1'>
                                <stop offset='0%' stopColor='rgba(5,150,105,0.28)' />
                                <stop offset='100%' stopColor='rgba(5,150,105,0.02)' />
                            </linearGradient>
                            <linearGradient id='salesAreaGradientNegative' x1='0' y1='0' x2='0' y2='1'>
                                <stop offset='0%' stopColor='rgba(220,38,38,0.22)' />
                                <stop offset='100%' stopColor='rgba(220,38,38,0.02)' />
                            </linearGradient>
                        </defs>
                        <line
                            x1={CHART_PADDING_X}
                            y1={chartData.zeroLineY}
                            x2={CHART_WIDTH - CHART_PADDING_X}
                            y2={chartData.zeroLineY}
                            className='salesChartZeroLine'
                        />
                        <path
                            d={chartData.areaPath}
                            className={`salesChartArea ${chartData.tone === 'negative' ? 'is-negative' : ''}`}
                        />
                        <polyline
                            points={chartData.pointsText}
                            className={`salesChartPolyline ${chartData.tone === 'positive' ? 'is-positive' : chartData.tone === 'negative' ? 'is-negative' : 'is-neutral'}`}
                        />
                        {chartData.points.map((point) => (
                            <g key={point.key}>
                                {viewMode === 'year' && (
                                    <text
                                        x={point.x}
                                        y={Math.max(12, point.y - 10)}
                                        className='salesChartPointLabel'
                                        textAnchor='middle'
                                    >
                                        {point.label}
                                    </text>
                                )}
                                <circle
                                    cx={point.x}
                                    cy={point.y}
                                    r='4'
                                    className={`salesChartPoint ${getPointToneClass(point.value)}`}
                                />
                                <title>{`${point.title}: ${formatMoney(point.value)}`}</title>
                            </g>
                        ))}
                    </svg>
                    <div className={`salesLineChartLabels is-${viewMode}`} style={{ gridTemplateColumns: `repeat(${chartData.series.length}, minmax(${chartLabelMinWidth}, 1fr))` }}>
                        {chartData.series.map((item) => (
                            <div className='salesLineChartLabel' key={item.key}>
                                <strong className={getPointToneClass(item.value)}>{formatCompactMoney(item.value)}</strong>
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
